import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Typography, Container, CssBaseline, Link, Button,
  CircularProgress,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import { motion, AnimatePresence } from 'framer-motion';
import AuthGuard from 'shared/components/AuthGuard';
import unsubscribeStyles from 'apps/account/pages/Unsubscribe/styles';
import { useMutation } from '@apollo/client';
import { UNSUBSCRIBE_MUTATION } from 'shared/queries/account/mutations';
import paths from 'paths';

const STATES = {
  CONFIRM: 'CONFIRM',
  SUCCESS: 'SUCCESS',
};

const Unsubscribe = () => {
  const classes = unsubscribeStyles();
  const history = useHistory();
  const [viewState, setViewState] = useState(STATES.CONFIRM);

  const [unsubscribeFromAllEmails, { loading }] = useMutation(UNSUBSCRIBE_MUTATION);

  const handleUnsubscribe = () => {
    unsubscribeFromAllEmails().then(() => {
      setViewState(STATES.SUCCESS);
    });
  };

  const handleCancel = () => {
    history.push(paths.rotationPaths.all.pattern);
  };

  return (
    <AuthGuard>
      <div className={classes.root}>
        <CssBaseline />
        <Container component="main" maxWidth="xs">
          <AnimatePresence exitBeforeEnter>
            {viewState === STATES.CONFIRM ? (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
              >
                <Box className={classes.paper}>
                  <div className={classes.iconWrapperWarning}>
                    <MailOutlineIcon style={{ fontSize: 48 }} />
                  </div>
                  <Typography className={classes.title} component="h1" variant="h4">
                    Unsubscribe
                  </Typography>
                  <Typography className={classes.message} variant="body1">
                    Are you sure you want to unsubscribe from all email notifications?
                    You might miss important updates about your rotations.
                  </Typography>
                  <div className={classes.buttonGroup}>
                    <Button
                      onClick={handleUnsubscribe}
                      className={classes.primaryButton}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Yes, Unsubscribe'}
                    </Button>
                    <Button
                      onClick={handleCancel}
                      className={classes.secondaryButton}
                      disabled={loading}
                    >
                      No, keep me subscribed
                    </Button>
                  </div>
                </Box>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <Box className={classes.paper}>
                  <motion.div
                    className={classes.iconWrapper}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      delay: 0.2,
                      type: 'spring',
                      stiffness: 260,
                      damping: 20,
                    }}
                  >
                    <CheckCircleOutlineIcon style={{ fontSize: 48 }} />
                  </motion.div>
                  <Typography className={classes.title} component="h1" variant="h4">
                    Unsubscribed
                  </Typography>
                  <Typography className={classes.message} variant="body1">
                    You have been successfully unsubscribed from all email notifications.
                    We&apos;re sorry to see you go!
                  </Typography>
                  <Link
                    href={paths.rotationPaths.all.pattern}
                    className={classes.linkButton}
                  >
                    Go to App
                  </Link>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Container>
      </div>
    </AuthGuard>
  );
};

export default Unsubscribe;
